import { useState, useMemo } from 'react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Terminal, CheckCircle, XCircle, Loader2, Filter } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ToolCallEvent } from '@/hooks/workspace/useWorkspaceSocket'

interface CalculationConsoleProps {
  events: ToolCallEvent[]
}

// Calc-only filter: any tool whose name starts with `calculate_`
// (calculate_standard, calculate_custom, calculate_hotspots,
// calculate_hotspots_custom, calculate_framework, ...) plus an
// explicit allowlist for non-prefixed calc-related tools.
const CALC_TOOL_ALLOWLIST = new Set([
  'get_task_status',
  'get_calculation_history',
  'get_lci_results',
])

function isCalcTool(toolName: string): boolean {
  return toolName.startsWith('calculate_') || CALC_TOOL_ALLOWLIST.has(toolName)
}

export function CalculationConsole({ events }: CalculationConsoleProps) {
  const [calcOnly, setCalcOnly] = useState(false)

  const displayEvents = useMemo(() => {
    const sorted = [...events].reverse() // newest first
    if (!calcOnly) return sorted
    return sorted.filter((e) => isCalcTool(e.data.tool_name))
  }, [events, calcOnly])

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-border bg-muted/30">
        <div className="flex items-center gap-1">
          <Button
            variant={calcOnly ? 'secondary' : 'ghost'}
            size="icon"
            className="h-6 w-6"
            onClick={() => setCalcOnly(!calcOnly)}
            title={calcOnly ? 'Show all events' : 'Show calculations only'}
          >
            <Filter className="h-3 w-3" />
          </Button>
        </div>
        <span className="text-[10px] text-muted-foreground">
          {displayEvents.length} event{displayEvents.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Console output */}
      <ScrollArea className="flex-1">
        <div className="p-2 font-mono text-xs space-y-0.5">
          {displayEvents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Terminal className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p>{calcOnly ? 'No calculation events yet' : 'No events yet'}</p>
            </div>
          ) : (
            displayEvents.map((event, i) => (
              <EventRow key={event.call_id || `${event.timestamp}-${i}`} event={event} />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function EventRow({ event }: { event: ToolCallEvent }) {
  const { tool_name, status, response_time_ms, error } = event.data
  const isCalc = isCalcTool(tool_name)
  const time = new Date(event.timestamp).toLocaleTimeString()

  const icon =
    status === 'completed' ? (
      <CheckCircle className="h-3 w-3 text-green-500" />
    ) : status === 'failed' ? (
      <XCircle className="h-3 w-3 text-red-500" />
    ) : (
      <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
    )

  const color =
    status === 'completed'
      ? 'text-green-600'
      : status === 'failed'
        ? 'text-red-600'
        : 'text-blue-600'

  return (
    <div className={cn('flex items-start gap-2 py-0.5 px-1 rounded', isCalc && 'bg-muted/40')}>
      <span className="text-[10px] text-muted-foreground/60 shrink-0 w-16">{time}</span>
      <span className="shrink-0 mt-0.5">{icon}</span>
      <div className="flex-1 min-w-0">
        <span className={cn('text-xs', color)}>{tool_name}</span>
        {response_time_ms != null && (
          <span className="text-[10px] text-muted-foreground ml-2">{response_time_ms}ms</span>
        )}
        {error && <p className="text-[10px] text-red-500 truncate">{String(error)}</p>}
      </div>
    </div>
  )
}
